import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { 
  Shield, CheckCircle, AlertCircle, Clock, FileText, Heart, Baby, RefreshCw, X, AlertTriangle, Trash2, Sparkles, Upload, Eye
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import ComplianceSettings from '@/components/ComplianceSettings';

const CompliancePage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [healthReminders, setHealthReminders] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);

  const categories = [
    { id: 'all', label: 'All Items' },
    { id: 'health_safety', label: 'Health & Safety' },
    { id: 'staff_qualifications', label: 'Staff Qualifications' },
    { id: 'record_keeping', label: 'Record Keeping' },
    { id: 'ratios', label: 'Ratios & Group Size' },
    { id: 'facility', label: 'Facility' }
  ];

  useEffect(() => {
    fetchCompliance();
    fetchHealthReminders();
  }, []);

  // Auto-refresh when tab regains focus + every 60s so compliance alerts
  // stay in sync with profile edits made in other tabs.
  useAutoRefresh(() => {
    fetchCompliance();
    fetchHealthReminders();
  }, 60000);

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API}/compliance`, getAuthHeaders());
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load compliance items');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthReminders = async () => {
    setHealthLoading(true);
    try {
      const response = await axios.get(`${API}/health-report-reminders`, getAuthHeaders());
      setHealthReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Failed to load health reminders');
    } finally {
      setHealthLoading(false);
    }
  };

  const runHealthCheck = async () => {
    setCheckingHealth(true);
    try {
      const response = await axios.post(`${API}/health-report-reminders/check-now`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchHealthReminders();
    } catch (error) {
      toast.error('Failed to run compliance check');
    } finally {
      setCheckingHealth(false);
    }
  };

  const dismissReminder = async (reminderId) => {
    try {
      await axios.post(`${API}/health-report-reminders/${reminderId}/dismiss`, {}, getAuthHeaders());
      toast.success('Reminder dismissed');
      fetchHealthReminders();
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const seedCompliance = async () => {
    try {
      await axios.post(`${API}/compliance/seed`, {}, getAuthHeaders());
      toast.success('Compliance items initialized');
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize compliance items');
    }
  };

  const toggleCompletion = async (itemId, currentStatus) => {
    try {
      await axios.put(
        `${API}/compliance/${itemId}?is_completed=${!currentStatus}`,
        {},
        getAuthHeaders()
      );
      toast.success(currentStatus ? 'Marked as incomplete' : 'Marked as complete');
      fetchCompliance();
    } catch (error) {
      toast.error('Failed to update compliance item');
    }
  };

  const deleteItem = async (itemId, title) => {
    if (!window.confirm(`Delete "${title}" from compliance?\n\nThis item will be removed from this center.`)) return;
    try {
      await axios.delete(`${API}/compliance/${itemId}`, getAuthHeaders());
      toast.success('Compliance item deleted');
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const cleanupLegacy = async () => {
    if (!window.confirm('Remove legacy items that are now tracked on staff/child profiles?\n\n• Staff Background Checks\n• CPR/First Aid Certification\n• Health Inspection\n• Child Health Records\n• Staff Training Records')) return;
    try {
      const res = await axios.post(`${API}/compliance/cleanup-legacy`, {}, getAuthHeaders());
      toast.success(`Removed ${res.data.count} legacy item${res.data.count === 1 ? '' : 's'}`);
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    }
  };

  const uploadProof = async (itemId, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/compliance/${itemId}/proof`, form, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Proof uploaded');
      fetchCompliance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  const viewProof = async (itemId, filename) => {
    try {
      const res = await axios.get(`${API}/compliance/${itemId}/proof`, {
        ...getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (error) {
      toast.error('Could not open proof file');
    }
  };

  const deleteProof = async (itemId) => {
    if (!window.confirm('Remove the attached proof file?')) return;
    try {
      await axios.delete(`${API}/compliance/${itemId}/proof`, getAuthHeaders());
      toast.success('Proof removed');
      fetchCompliance();
    } catch (error) {
      toast.error('Could not remove proof');
    }
  };

  const dueInfo = (item) => {
    if (!item.reset_days || !item.completed_date) return null;
    try {
      const completed = new Date(item.completed_date);
      const expires = new Date(completed.getTime() + item.reset_days * 86400000);
      const days = Math.round((expires - new Date()) / 86400000);
      return { expires, days };
    } catch (e) {
      return null;
    }
  };

  const filteredItems = selectedCategory === 'all' 
    ? items 
    : items.filter(item => item.category === selectedCategory);

  const completedCount = items.filter(i => i.is_completed).length;
  const totalCount = items.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout title="DCF Compliance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="DCF Compliance">
      <div className="space-y-6" data-testid="compliance-page">
        {/* Compliance Alert Settings (admin-only, collapsible) */}
        <ComplianceSettings onChange={() => { fetchCompliance(); fetchHealthReminders(); }} />

        {/* Progress Overview */}
        <Card className="card-clay">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                  <Shield className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl">Wisconsin DCF Compliance</h2>
                  <p className="text-muted-foreground">Track your compliance with state regulations</p>
                </div>
              </div>
              {totalCount === 0 && user.role === 'admin' && (
                <Button onClick={seedCompliance} data-testid="seed-compliance-btn">
                  Initialize Compliance Items
                </Button>
              )}
            </div>

            {totalCount > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Overall Compliance</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-lg">{completionPercentage}%</span>
                    {items.some(i => ['Staff Background Checks','CPR/First Aid Certification','Health Inspection','Child Health Records','Staff Training Records'].includes(i.title)) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={cleanupLegacy}
                        className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="cleanup-legacy-btn"
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        Clean up legacy items
                      </Button>
                    )}
                  </div>
                </div>
                <Progress value={completionPercentage} className="h-4" />
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    {completedCount} Completed
                  </span>
                  <span className="flex items-center gap-1 text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    {totalCount - completedCount} Pending
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {totalCount > 0 && (
          <>
            {/* Health Report Reminders */}
            <Card className="card-clay border-l-4 border-l-blue-500" data-testid="health-report-reminders">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Heart className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Child Health Report Reminders</CardTitle>
                      <p className="text-sm text-muted-foreground">DCF-F-CFS0060 compliance tracking</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={runHealthCheck}
                    disabled={checkingHealth}
                    data-testid="run-health-check-btn"
                  >
                    <RefreshCw className={cn("w-4 h-4 mr-1", checkingHealth && "animate-spin")} />
                    {checkingHealth ? 'Checking...' : 'Check Now'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Schedule Rules */}
                <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm space-y-1">
                  <p className="font-medium text-muted-foreground">DCF Health Report Schedule:</p>
                  <p><Baby className="w-3 h-3 inline mr-1" /><span className="font-medium">Birth - 2 years:</span> Required at admission (within 3 months), then every <span className="font-semibold text-blue-700">6 months</span></p>
                  <p><Baby className="w-3 h-3 inline mr-1" /><span className="font-medium">Ages 2 - 5:</span> Required at admission (within 3 months), then every <span className="font-semibold text-blue-700">2 years</span></p>
                </div>

                {healthLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : healthReminders.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">All health reports are up to date</p>
                    <p className="text-sm">No pending health report reminders</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                        {healthReminders.filter(r => r.urgency === 'overdue').length} Overdue
                      </Badge>
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        {healthReminders.filter(r => r.urgency === 'warning').length} Due Soon
                      </Badge>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {healthReminders.filter(r => r.urgency === 'normal').length} Upcoming
                      </Badge>
                    </div>
                    {healthReminders.map((reminder) => (
                      <div
                        key={reminder.id}
                        className={cn(
                          "flex items-start justify-between p-3 rounded-lg border",
                          reminder.urgency === 'overdue' && "bg-red-50 border-red-200",
                          reminder.urgency === 'warning' && "bg-amber-50 border-amber-200",
                          reminder.urgency === 'normal' && "bg-blue-50 border-blue-200"
                        )}
                        data-testid={`health-reminder-${reminder.id}`}
                      >
                        <div className="flex items-start gap-3">
                          {reminder.urgency === 'overdue' ? (
                            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                          ) : reminder.urgency === 'warning' ? (
                            <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                          ) : (
                            <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                          )}
                          <div>
                            <p className="font-semibold text-sm">{reminder.child_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Age: {reminder.age_label} | Schedule: {reminder.schedule}
                            </p>
                            {reminder.due_date && (
                              <p className={cn(
                                "text-xs font-semibold mt-1",
                                reminder.urgency === 'overdue' ? 'text-red-700'
                                  : reminder.urgency === 'warning' ? 'text-amber-700'
                                  : 'text-blue-700'
                              )}>
                                Due: {new Date(reminder.due_date + 'T12:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                            <p className="text-xs mt-1">{reminder.reason}</p>
                            {reminder.enrollment_date && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Admitted: {new Date(reminder.enrollment_date + 'T12:00:00').toLocaleDateString()}
                              </p>
                            )}
                            {reminder.last_report_date && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                Last report: {new Date(reminder.last_report_date + 'T12:00:00').toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 shrink-0"
                          onClick={() => dismissReminder(reminder.id)}
                          title="Dismiss reminder"
                          data-testid={`dismiss-reminder-${reminder.id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => {
                const categoryCount = category.id === 'all' 
                  ? items.length 
                  : items.filter(i => i.category === category.id).length;
                const categoryCompleted = category.id === 'all'
                  ? completedCount
                  : items.filter(i => i.category === category.id && i.is_completed).length;
                
                return (
                  <Button
                    key={category.id}
                    variant={selectedCategory === category.id ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(category.id)}
                    className="rounded-full"
                    data-testid={`category-${category.id}`}
                  >
                    {category.label}
                    <span className="ml-2 text-xs opacity-70">
                      {categoryCompleted}/{categoryCount}
                    </span>
                  </Button>
                );
              })}
            </div>

            {/* Compliance Items */}
            <div className="space-y-3">
              {filteredItems.map((item) => (
                <Card 
                  key={item.id} 
                  className={cn(
                    "card-clay transition-all",
                    item.is_completed && "bg-green-50 border-green-200"
                  )}
                  data-testid={`compliance-item-${item.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id={item.id}
                        checked={item.is_completed}
                        onCheckedChange={() => toggleCompletion(item.id, item.is_completed)}
                        className="mt-1"
                        data-testid={`compliance-checkbox-${item.id}`}
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <label 
                              htmlFor={item.id}
                              className={cn(
                                "font-semibold cursor-pointer",
                                item.is_completed && "line-through text-muted-foreground"
                              )}
                            >
                              {item.title}
                            </label>
                            <p className="text-sm text-muted-foreground mt-1">
                              {item.description}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-xs bg-muted px-2 py-1 rounded font-mono">
                              {item.regulation_code}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => { e.preventDefault(); deleteItem(item.id, item.title); }}
                              title="Delete this item"
                              data-testid={`compliance-delete-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {item.is_completed && item.completed_date && (
                          <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            Completed on {item.completed_date}
                          </p>
                        )}

                        {/* Due-date / overdue badge for items with a retest cadence */}
                        {(() => {
                          const d = dueInfo(item);
                          if (!d) return null;
                          const overdue = d.days < 0;
                          const dueSoon = d.days >= 0 && d.days <= 60;
                          return (
                            <div className="mt-2">
                              {overdue ? (
                                <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border border-red-200 text-xs" data-testid={`compliance-overdue-${item.id}`}>
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Overdue by {Math.abs(d.days)} days — retest required
                                </Badge>
                              ) : dueSoon ? (
                                <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 border border-amber-200 text-xs" data-testid={`compliance-due-soon-${item.id}`}>
                                  <Clock className="w-3 h-3 mr-1" />
                                  Due in {d.days} days (by {d.expires.toISOString().slice(0, 10)})
                                </Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  Next retest by {d.expires.toISOString().slice(0, 10)}
                                </span>
                              )}
                            </div>
                          );
                        })()}

                        {/* Proof of completion upload / view */}
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {item.proof_filename ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                onClick={() => viewProof(item.id, item.proof_filename)}
                                data-testid={`compliance-view-proof-${item.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" /> View proof ({item.proof_filename})
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-red-500"
                                onClick={() => deleteProof(item.id)}
                                data-testid={`compliance-remove-proof-${item.id}`}
                              >
                                Remove
                              </Button>
                            </>
                          ) : (
                            <label className="inline-flex items-center gap-1 text-xs cursor-pointer text-primary hover:underline" data-testid={`compliance-upload-proof-label-${item.id}`}>
                              <Upload className="w-3 h-3" /> Upload proof (PDF or photo)
                              <input
                                type="file"
                                className="hidden"
                                accept="application/pdf,image/*"
                                onChange={(e) => uploadProof(item.id, e.target.files?.[0])}
                                data-testid={`compliance-upload-proof-${item.id}`}
                              />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {totalCount === 0 && (
          <>
            {/* Health Report Reminders - show even with no general compliance items */}
            <Card className="card-clay border-l-4 border-l-blue-500" data-testid="health-report-reminders-empty">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Heart className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Child Health Report Reminders</CardTitle>
                      <p className="text-sm text-muted-foreground">DCF-F-CFS0060 compliance tracking</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={runHealthCheck}
                    disabled={checkingHealth}
                  >
                    <RefreshCw className={cn("w-4 h-4 mr-1", checkingHealth && "animate-spin")} />
                    {checkingHealth ? 'Checking...' : 'Check Now'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-muted/50 rounded-lg p-3 mb-4 text-sm space-y-1">
                  <p className="font-medium text-muted-foreground">DCF Health Report Schedule:</p>
                  <p><Baby className="w-3 h-3 inline mr-1" /><span className="font-medium">Birth - 2 years:</span> Required at admission (within 3 months), then every <span className="font-semibold text-blue-700">6 months</span></p>
                  <p><Baby className="w-3 h-3 inline mr-1" /><span className="font-medium">Ages 2 - 5:</span> Required at admission (within 3 months), then every <span className="font-semibold text-blue-700">2 years</span></p>
                </div>
                {healthReminders.length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-500" />
                    <p className="font-medium">All health reports are up to date</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {healthReminders.map((reminder) => (
                      <div
                        key={reminder.id}
                        className={cn(
                          "flex items-start justify-between p-3 rounded-lg border",
                          reminder.urgency === 'overdue' && "bg-red-50 border-red-200",
                          reminder.urgency === 'warning' && "bg-amber-50 border-amber-200",
                          reminder.urgency === 'normal' && "bg-blue-50 border-blue-200"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          {reminder.urgency === 'overdue' ? (
                            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                          ) : (
                            <Clock className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                          )}
                          <div>
                            <p className="font-semibold text-sm">{reminder.child_name}</p>
                            <p className="text-xs text-muted-foreground">Age: {reminder.age_label} | {reminder.schedule}</p>
                            <p className="text-xs mt-1">{reminder.reason}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => dismissReminder(reminder.id)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="card-clay">
              <CardContent className="p-12 text-center">
                <Shield className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="font-heading font-bold text-xl mb-2">No General Compliance Items</h3>
                <p className="text-muted-foreground mb-6">
                  Initialize Wisconsin DCF compliance requirements to get started.
                </p>
                {user.role === 'admin' && (
                  <Button onClick={seedCompliance} data-testid="empty-seed-btn">
                    Initialize Compliance Items
                  </Button>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default CompliancePage;
